
import { defineComponent, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { getModalInfo } from '@/api/modal-info.api'
import { DateTime } from 'luxon'
import { useMedia } from '@/utils/useMedia'
import { getUser, useEvent } from '@coderhouse/common'
import { getAvailableBalances } from '@/api/available-balance.api'
import { confirmPurchase, getPurchase, getUpsellingCoupon } from '@/api/purchase.api'

const __default__ = defineComponent({
  props: {
    suggestedCourseID: {
      type: String,
      required: true,
      default: ''
    },
    show: {
      type: Boolean,
      required: true,
      default: false
    },
    user: {
      type: Object,
      required: true,
      default: Object
    },
    homeToast: {
      type: Object,
      required: true
    },
    topModal: {
      type: String,
      required: true,
      default: '0px'
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n()
    const user = ref()
    const availableBalance = ref()
    const purchase = ref()
    const couponCode = ref()
    const isThereAvailableBalanceWithCurrency = ref(false)
    const isThereFreePurchase = ref(false)
    const freePurchaseAlias = ref()
    const userAvailableBalanceAlias = ref()
    const modalInfo = ref()
    const isMediumDesktop = useMedia('(min-width: 770px)')
    const isMobile = useMedia('(max-width: 760px)')
    const loading = ref(true)
    const disabledButton = ref(false)
    const homeToast = ref(props.homeToast)

    const formatDays = (items: Array<string>) => {
      let localFormat = ''
      if (items.length === 1) {
        localFormat = t(items[0])
      } else {
        const transaltedDays = items.map(item => {
          item = t(item)
          return item
        })
        localFormat = transaltedDays.join(' ' + t('ch-course.and') + ' ')
      }
      return localFormat
    }

    const formatDate = (date: string) => {
      const lang = props.user?.country === 'br' ? 'pt' : 'es'
      return DateTime.fromISO(date).setLocale(lang).toFormat('dd/MM')
    }

    const formatTime = (time: string) => {
      return DateTime.fromISO(time).toFormat('hh:mm')
    }

    onMounted(async () => {
      user.value = await getUser()
      modalInfo.value = await modalData()

      await Promise.all([getUserAvailableBalance(), getUpsellingCouponForCurrentCountry()])

      await getPurchaseInfo()

      loading.value = false
    })

    const getUpsellingCouponForCurrentCountry = async () => {
      const upsellingCoupon = await getUpsellingCoupon(props.user?.country)
      if (upsellingCoupon && upsellingCoupon.cupon) {
        couponCode.value = upsellingCoupon.cupon._id
      }
    }

    const getUserAvailableBalance = async () => {
      const allAvailableBalances = await getAvailableBalances(user.value.id)

      if (allAvailableBalances.length > 0) {
        const ab = allAvailableBalances.filter(ab => !ab.claimed && ab.enable && ab.management == 'free')

        if (ab.length > 0) {
          availableBalance.value = ab[0]
          if (!availableBalance.value.onlyForLevel) {
            isThereAvailableBalanceWithCurrency.value = true
            userAvailableBalanceAlias.value =
              availableBalance.value.currency.symbol + availableBalance.value.price + ' ' + availableBalance.value.currency.iso_code
          }
        }
      }
    }
    const getPurchaseInfo = async () => {
      let nameSplit = user.value.fullName.split(' ')
      if (modalInfo.value.dates.length > 0) {
        let body = {
          buyer: {
            _id: user.value.id,
            email: user.value.email,
            profile: {
              first_name: nameSplit[0],
              last_name: nameSplit[1]
            }
          },
          stu: user.value.id,
          country: user.value.country,
          products: [props.suggestedCourseID],
          courses: [modalInfo.value.dates[0]._id],
          cupons: couponCode.value ? [couponCode.value] : [],
          userAvailableBalanceId: availableBalance.value ? availableBalance.value.id : null
        }

        purchase.value = await getPurchase(body)

        if (purchase.value && purchase.value.finalPricing.fullFinalPrice === 0) {
          isThereFreePurchase.value = true
          if (availableBalance.value.onlyForLevel) {
            freePurchaseAlias.value = t('products.modal.available-balance-free-course')
            return
          }
          if (purchase.value && purchase.value.finalPricing.userAvailableBalance < availableBalance.value.price) {
            const newBalance = availableBalance.value.price - purchase.value.finalPricing.userAvailableBalance
            freePurchaseAlias.value =
              t('products.modal.available-balance-free-course-with-rest') +
              availableBalance.value.currency.symbol +
              newBalance +
              ' ' +
              availableBalance.value.currency.iso_code
          } else {
            freePurchaseAlias.value = t('products.modal.available-balance-free-course')
          }
        }
      }
    }

    const enrollUser = async (link: string) => {
      if (freePurchaseAlias.value) {
        if (confirm(t('products.modal.confirm-enroll-message'))) {
          disabledButton.value = true
          let nameSplit = user.value.fullName.split(' ')
          let body = {
            buyer: {
              _id: user.value.id,
              email: user.value.email,
              profile: {
                first_name: nameSplit[0],
                last_name: nameSplit[1]
              }
            },
            stu: user.value.id,
            country: user.value.country,
            products: [props.suggestedCourseID],
            courses: [modalInfo.value.dates[0]._id],
            cupons: couponCode.value ? [couponCode.value] : [],
            userAvailableBalanceId: availableBalance.value ? availableBalance.value.id : null
          }
          try {
            let purchase = await confirmPurchase(body)
            if (purchase) {
              homeToast.value.label = t('products.modal.success-enroll')
              homeToast.value.type = 'success'
              homeToast.value.toggle = true
            }
          } catch (err) {
            homeToast.value.toggle = false
            homeToast.value.toggle = true
            homeToast.value.label = t('products.modal.fail-enroll')
            homeToast.value.type = 'danger'
          }
        } else {
          return
        }
        closeModal()
        window.location.reload()
        return
      }
      redirect(link)
    }

    const modalData = async () => {
      const res = await getModalInfo(props.suggestedCourseID, props.user?.country)
      return res
    }

    const closeModal = () => {
      emit('close')
    }

    const redirect = (link: string) => {
      useEvent('trackEvent', 'cta', 'modal_suggestedCourse_linkDate', 'redirects_checkout')
      if (link) {
        const hasAvailableBalance = isThereAvailableBalanceWithCurrency.value
        window.open(
          `${link}${hasAvailableBalance ? `&pab=${availableBalance.value.id}` : ''}${user.value ? `&stu=${user.value.id}` : ''}`
        )
      }
      return
    }

    return {
      t,
      closeModal,
      modalInfo,
      formatDays,
      formatDate,
      formatTime,
      enrollUser,
      redirect,
      isMediumDesktop,
      isMobile,
      loading,
      isThereFreePurchase,
      isThereAvailableBalanceWithCurrency,
      userAvailableBalanceAlias,
      freePurchaseAlias,
      availableBalance,
      disabledButton
    }
  }
})

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "b9b63330": (_ctx.topModal)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__